import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtINR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import {
  ArrowClockwise, Coins, Bank, CaretDown, CaretRight,
  ArrowUp, ArrowDown, Warning, CheckCircle, Trash, CalendarBlank,
} from "@phosphor-icons/react";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString("en-IN", {
      day: "2-digit", month: "short", year: "2-digit",
    });
  } catch { return s; }
};

// Default range = current month
const defaultRange = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const iso  = (d) => d.toISOString().slice(0, 10);
  return { from: iso(from), to: iso(to) };
};

const isoToDate = (s) => (s ? new Date(s + "T00:00:00") : undefined);
const dateToIso = (d) => (d ? d.toISOString().slice(0, 10) : "");

export default function CashCollectionTab({ refreshKey }) {
  const init = defaultRange();
  const [startDate, setStartDate] = useState(init.from);
  const [endDate,   setEndDate]   = useState(init.to);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedOutlet, setExpandedOutlet] = useState(null);
  const [uploads, setUploads] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // We only auto-derive the range from uploads ONCE per mount so the user's
  // manual picks / preset clicks stick after that.
  const [rangeAutoSet, setRangeAutoSet] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/cash-collection", {
        params: { start_date: startDate, end_date: endDate },
      });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cash collection");
    } finally { setLoading(false); }
  }, [startDate, endDate]);

  const loadUploads = useCallback(async () => {
    try {
      const r = await api.get("/pos-sales");
      const rows = r.data?.rows || [];
      setUploads(rows);
      // On the very first load, if the user hasn't already tweaked the range
      // and screenshots exist, snap the window to the union of every uploaded
      // screenshot's period. This makes both the sales aggregation AND the
      // HDFC cash-deposit filter align with what the user actually captured
      // from PetPooja — no manual date-picking required.
      if (!rangeAutoSet && rows.length > 0) {
        const froms = rows.map((r) => r.period_from).filter(Boolean).sort();
        const tos   = rows.map((r) => r.period_to).filter(Boolean).sort();
        if (froms.length && tos.length) {
          setStartDate(froms[0]);
          setEndDate(tos[tos.length - 1]);
          setRangeAutoSet(true);
        }
      }
    } catch {
      setUploads([]);
    }
  }, [rangeAutoSet]);

  useEffect(() => { load(); loadUploads(); }, [load, loadUploads, refreshKey]);

  const deleteUpload = async (id) => {
    try {
      await api.delete(`/pos-sales/${id}`);
      toast.success("Sales row deleted");
      load();
      loadUploads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const clearAllUploads = async () => {
    if (!window.confirm(`Delete ALL ${uploads.length} PetPooja Sales uploads? This cannot be undone.`)) return;
    try {
      const r = await api.delete("/pos-sales/clear");
      toast.success(`Cleared ${r.data?.deleted ?? 0} uploads`);
      setRangeAutoSet(false);  // next upload should re-auto-fill the range
      load();
      loadUploads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const summary = useMemo(() => {
    if (!data) return null;
    const s = data.total_cash_sales || 0;
    const d = data.total_deposits || 0;
    const v = data.variance || 0;
    const pct = data.variance_pct;
    let status = "match";
    if (Math.abs(v) < 100) status = "match";
    else if (v > 0) status = "under_deposited"; // sales > deposits
    else status = "over_deposited";              // sales < deposits
    return { s, d, v, pct, status };
  }, [data]);

  const applyPreset = (preset) => {
    const now = new Date();
    let from, to;
    if (preset === "this_month") {
      from = new Date(now.getFullYear(), now.getMonth(), 1);
      to   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    } else if (preset === "last_month") {
      from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      to   = new Date(now.getFullYear(), now.getMonth(), 0);
    } else if (preset === "last_30") {
      to   = now;
      from = new Date(now); from.setDate(from.getDate() - 29);
    } else if (preset === "ytd") {
      from = new Date(now.getFullYear(), 0, 1);
      to   = now;
    } else { return; }
    const iso = (d) => d.toISOString().slice(0, 10);
    setStartDate(iso(from));
    setEndDate(iso(to));
  };

  return (
    <div className="space-y-4" data-testid="cash-collection-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading font-bold text-lg text-slate-100 flex items-center gap-2">
            <Coins size={16} weight="duotone" className="text-cyan-300" />
            Cash Collection Status
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Reconciles PetPooja Cash Sales (per outlet, via screenshot OCR) against HDFC Cash-Deposit credits for the same window.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}
          className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-cyan-100"
          data-testid="cash-collection-refresh">
          <ArrowClockwise size={13} weight="bold" className="mr-1" />Refresh
        </Button>
      </div>

      {/* Date range controls */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1">
            Date Range
            {rangeAutoSet && uploads.length > 0 && (
              <span className="ml-2 normal-case tracking-normal text-[9px] font-semibold text-cyan-300/80" title="Auto-derived from uploaded PetPooja screenshots">
                (auto · from PetPooja uploads)
              </span>
            )}
          </label>
          <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="h-9 border-slate-700 text-slate-100 bg-slate-950 hover:bg-slate-900 hover:text-cyan-100 font-mono text-[12px] justify-start w-72"
                data-testid="cash-collection-date-range-trigger"
              >
                <CalendarBlank size={13} weight="bold" className="mr-2 text-cyan-300" />
                {startDate && endDate
                  ? `${fmtDate(startDate)}  →  ${fmtDate(endDate)}`
                  : "Pick a range"}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 bg-slate-950 border-slate-700 text-slate-100 w-auto"
              align="start"
              data-testid="cash-collection-date-range-popover"
            >
              <Calendar
                mode="range"
                numberOfMonths={2}
                defaultMonth={isoToDate(startDate)}
                selected={{ from: isoToDate(startDate), to: isoToDate(endDate) }}
                onSelect={(range) => {
                  if (range?.from) setStartDate(dateToIso(range.from));
                  if (range?.to)   setEndDate(dateToIso(range.to));
                  if (range?.from && range?.to) setPickerOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex items-end gap-1">
          {[
            { id: "this_month", label: "This Mon" },
            { id: "last_month", label: "Last Mon" },
            { id: "last_30",    label: "30d" },
            { id: "ytd",        label: "YTD" },
          ].map((p) => (
            <Button key={p.id} variant="outline" size="sm" onClick={() => applyPreset(p.id)}
              className="h-9 border-slate-700 text-slate-300 bg-slate-900 hover:bg-slate-800 hover:text-cyan-100 text-[11px]"
              data-testid={`cash-collection-preset-${p.id}`}>
              {p.label}
            </Button>
          ))}
        </div>
        <div className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">
          {loading ? "Loading…" : `${data?.outlet_count ?? 0} outlets · ${data?.deposit_count ?? 0} deposits`}
        </div>
      </div>

      {/* KPI cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cash-collection-summary">
          <KpiCard
            label="Total Cash Sales"
            sub="Sum of PetPooja Cash (all outlets, in window)"
            value={summary.s}
            accent="emerald"
            icon={<Coins size={14} weight="duotone" />}
            testId="cash-collection-total-sales"
          />
          <KpiCard
            label="Total HDFC Cash Deposits"
            sub="Bank credits matching CASH DEPOSIT / CDM"
            value={summary.d}
            accent="cyan"
            icon={<Bank size={14} weight="duotone" />}
            testId="cash-collection-total-deposits"
          />
          <VarianceCard
            variance={summary.v}
            pct={summary.pct}
            status={summary.status}
          />
        </div>
      )}

      {/* Outlets table */}
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-200 flex items-center gap-1.5">
            <Coins size={13} weight="duotone" /> Outlet-wise Cash Sales
          </div>
          <div className="text-[10px] text-slate-500">Click a row to expand upload history</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="cash-collection-outlets-table">
            <thead className="bg-slate-900/50">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                <th className="text-left px-3 py-2 w-8"></th>
                <th className="text-left px-3 py-2">Outlet</th>
                <th className="text-right px-3 py-2">Cash</th>
                <th className="text-right px-3 py-2">Card</th>
                <th className="text-right px-3 py-2">Online</th>
                <th className="text-right px-3 py-2">Other</th>
                <th className="text-right px-3 py-2">Total</th>
                <th className="text-right px-3 py-2">Uploads</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">Loading…</td></tr>
              )}
              {!loading && (!data?.outlets || data.outlets.length === 0) && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">
                  No PetPooja Sales screenshots overlap this window. Upload a screenshot in Data → Uploads → PetPooja sales.
                </td></tr>
              )}
              {!loading && data?.outlets?.map((o, i) => {
                const open = expandedOutlet === o.outlet_label;
                return (
                  <React.Fragment key={o.outlet_label}>
                    <tr
                      onClick={() => setExpandedOutlet(open ? null : o.outlet_label)}
                      className="border-t border-slate-800 hover:bg-slate-900/60 cursor-pointer"
                      data-testid={`cash-collection-outlet-row-${i}`}
                    >
                      <td className="px-3 py-2 text-slate-500">
                        {open ? <CaretDown size={11} weight="bold" /> : <CaretRight size={11} weight="bold" />}
                      </td>
                      <td className="px-3 py-2 font-semibold text-slate-100">{o.outlet_label}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums font-bold text-emerald-300">{fmtINR(o.cash_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{fmtINR(o.card_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{fmtINR(o.online_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-400">{fmtINR(o.other_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-200">{fmtINR(o.total_sales)}</td>
                      <td className="px-3 py-2 text-right font-mono tabular-nums text-slate-500">{o.uploads?.length || 0}</td>
                    </tr>
                    {open && (
                      <tr className="bg-slate-950/60" data-testid={`cash-collection-outlet-detail-${i}`}>
                        <td></td>
                        <td colSpan={7} className="px-3 py-2">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-1.5">Uploaded screenshots ({o.uploads?.length || 0})</div>
                          <div className="space-y-1">
                            {(o.uploads || []).map((u) => (
                              <div key={u.id} className="flex items-center gap-2 text-[11px] font-mono">
                                <span className="text-slate-400 w-40">{u.period_from} → {u.period_to}</span>
                                <span className="text-emerald-300 w-24 text-right">Cash {fmtINR(u.cash_sales)}</span>
                                <span className="text-slate-500 w-28 text-right">Total {fmtINR(u.total_sales)}</span>
                                <span className="text-slate-600 flex-1 text-right">uploaded {fmtDate(u.uploaded_at)}</span>
                                <button
                                  onClick={(e) => { e.stopPropagation(); deleteUpload(u.id); }}
                                  className="text-slate-500 hover:text-rose-300"
                                  title="Delete this upload"
                                  data-testid={`cash-collection-delete-upload-${u.id}`}
                                >
                                  <Trash size={12} weight="bold" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {!loading && data?.outlets?.length > 0 && (
                <tr className="border-t-2 border-cyan-800 bg-slate-900/80 font-bold">
                  <td></td>
                  <td className="px-3 py-2 text-cyan-200 uppercase tracking-wider text-[10px]">Total</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-emerald-200">{fmtINR(data.total_cash_sales)}</td>
                  <td colSpan={5}></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Bank deposits table */}
      <div className="rounded-md border border-slate-700 overflow-hidden">
        <div className="px-3 py-2 bg-slate-900/80 border-b border-slate-800 flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-200 flex items-center gap-1.5">
            <Bank size={13} weight="duotone" /> HDFC Cash Deposit Credits ({data?.deposit_count ?? 0})
          </div>
          <div className="text-[10px] text-slate-500 font-mono">
            keywords: {(data?.keywords || []).join(" · ")}
          </div>
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="cash-collection-deposits-table">
            <thead className="bg-slate-900/50 sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
                <th className="text-left px-3 py-2 w-24">Date</th>
                <th className="text-left px-3 py-2">Description</th>
                <th className="text-right px-3 py-2 w-32">Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={3} className="text-center py-6 text-slate-500">Loading…</td></tr>
              )}
              {!loading && (data?.deposits?.length ?? 0) === 0 && (
                <tr><td colSpan={3} className="text-center py-6 text-slate-500">No cash-deposit credits found in this window.</td></tr>
              )}
              {!loading && data?.deposits?.map((d, i) => (
                <tr key={d.id || i} className="border-t border-slate-800 hover:bg-slate-900/60" data-testid={`cash-collection-deposit-row-${i}`}>
                  <td className="px-3 py-2 font-mono text-slate-300">{fmtDate(d.date)}</td>
                  <td className="px-3 py-2 text-slate-200 font-mono text-[11px] truncate max-w-[560px]" title={d.description}>{d.description}</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums font-semibold text-cyan-200">{fmtINR(d.amount)}</td>
                </tr>
              ))}
              {!loading && (data?.deposits?.length ?? 0) > 0 && (
                <tr className="border-t-2 border-cyan-800 bg-slate-900/80 font-bold">
                  <td></td>
                  <td className="px-3 py-2 text-cyan-200 uppercase tracking-wider text-[10px]">Total</td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-cyan-200">{fmtINR(data.total_deposits)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Uploads catalogue */}
      <details className="rounded-md border border-slate-800 bg-slate-900/40 overflow-hidden" data-testid="cash-collection-uploads-catalog">
        <summary className="px-3 py-2 cursor-pointer text-[11px] uppercase tracking-wider font-bold text-slate-300 hover:text-cyan-200 flex items-center justify-between">
          <span>All PetPooja Sales uploads ({uploads.length})</span>
          {uploads.length > 0 && (
            <button
              onClick={(e) => { e.preventDefault(); clearAllUploads(); }}
              className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-800 bg-rose-950/40 text-rose-300 hover:bg-rose-900/60 hover:text-rose-100 text-[10px] normal-case font-semibold tracking-normal"
              title="Delete every PetPooja Sales upload"
              data-testid="cash-collection-clear-all"
            >
              <Trash size={11} weight="bold" />
              Clear all uploads
            </button>
          )}
        </summary>
        <div className="max-h-64 overflow-y-auto border-t border-slate-800">
          <table className="w-full text-xs">
            <thead className="bg-slate-900/60 sticky top-0">
              <tr className="text-[10px] uppercase tracking-wider text-slate-400">
                <th className="text-left px-3 py-1.5">Outlet</th>
                <th className="text-left px-3 py-1.5">Period</th>
                <th className="text-right px-3 py-1.5">Cash</th>
                <th className="text-right px-3 py-1.5">Total</th>
                <th className="text-left px-3 py-1.5">Uploaded</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {uploads.length === 0 && (
                <tr><td colSpan={6} className="text-center py-4 text-slate-500">Nothing uploaded yet.</td></tr>
              )}
              {uploads.map((u) => (
                <tr key={u.id} className="border-t border-slate-800 hover:bg-slate-900/60">
                  <td className="px-3 py-1.5 font-semibold text-slate-200">{u.outlet_label}</td>
                  <td className="px-3 py-1.5 font-mono text-slate-400">{u.period_from} → {u.period_to}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-emerald-300">{fmtINR(u.cash_sales)}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-300">{fmtINR(u.total_sales)}</td>
                  <td className="px-3 py-1.5 text-slate-500 text-[10px]">{fmtDate(u.uploaded_at)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button onClick={() => deleteUpload(u.id)} className="text-slate-500 hover:text-rose-300" title="Delete">
                      <Trash size={12} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </div>
  );
}

function KpiCard({ label, sub, value, accent, icon, testId }) {
  const tones = {
    emerald: "border-emerald-800 bg-emerald-950/40 text-emerald-200",
    cyan:    "border-cyan-800    bg-cyan-950/40    text-cyan-200",
  };
  return (
    <div className={`rounded-md border p-3 ${tones[accent]}`} data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        {icon}<span>{label}</span>
      </div>
      <div className="mt-1 font-mono text-2xl font-bold tabular-nums">{fmtINR(value)}</div>
      <div className="text-[10px] opacity-60 mt-0.5">{sub}</div>
    </div>
  );
}

function VarianceCard({ variance, pct, status }) {
  const isMatch    = status === "match";
  const isUnderDep = status === "under_deposited";
  const tone = isMatch
    ? "border-emerald-800 bg-emerald-950/40 text-emerald-200"
    : isUnderDep
      ? "border-rose-800 bg-rose-950/40 text-rose-200"
      : "border-amber-800 bg-amber-950/40 text-amber-200";
  const label = isMatch ? "Reconciled" : isUnderDep ? "Under-deposited" : "Over-deposited";
  const Icon = isMatch ? CheckCircle : Warning;
  const ArrowIcon = variance > 0 ? ArrowUp : ArrowDown;
  return (
    <div className={`rounded-md border p-3 ${tone}`} data-testid="cash-collection-variance">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon size={14} weight="duotone" /><span>Variance ({label})</span>
      </div>
      <div className="mt-1 font-mono text-2xl font-bold tabular-nums flex items-center gap-1">
        {variance !== 0 && <ArrowIcon size={16} weight="bold" />}
        {fmtINR(Math.abs(variance))}
      </div>
      <div className="text-[10px] opacity-60 mt-0.5">
        {pct === null || pct === undefined ? "—" : `${Math.abs(pct).toFixed(1)}% of cash sales`}
        {isUnderDep && " · sales exceed deposits"}
        {status === "over_deposited" && " · deposits exceed sales"}
      </div>
    </div>
  );
}
